import { useState, type FormEvent } from "react";
import { Navigate } from "react-router";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ErrorState } from "@/components/ErrorState";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import {
  useInvite,
  useRemoveTeammate,
  useResendInvite,
  useTeam,
  type TeamRole,
  type Teammate,
} from "@/api/hooks/use-team";
import { useAuth } from "@/contexts/AuthContext";
import { leaveFor } from "@/lib/auth";
import { toUserMessage } from "@/lib/errors";

const ROLES: { id: TeamRole; label: string }[] = [
  { id: "viewer", label: "Viewer" },
  { id: "editor", label: "Editor" },
  { id: "admin", label: "Admin" },
];
const ROLE_LABELS = new Map(ROLES.map((r) => [r.id, r.label]));

const inputCls =
  "rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40";
const buttonCls =
  "rounded-md border border-line px-3 py-1.5 text-sm text-fg transition-colors hover:bg-canvas disabled:cursor-not-allowed disabled:opacity-40";

/** /team (admins): invite teammates, resend or cancel invites, remove Members.
 * Other Members who open it go to Overview. The Sidebar hides the link from
 * them, and the server refuses them anyway. */
export function Team() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSkeleton rows={3} />;
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return <TeamAdmin />;
}

function TeamAdmin() {
  const { data, isLoading, isError, refetch } = useTeam();
  const invite = useInvite();
  const resend = useResendInvite();
  const remove = useRemoveTeammate();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("viewer");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState<Teammate | null>(null);

  function report(message: string) {
    setError("");
    setNotice(message);
  }

  function fail(err: unknown) {
    setNotice("");
    setError(toUserMessage(err));
  }

  function handleInvite(e: FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    invite.mutate(
      { email: address, role },
      {
        onSuccess: (sent) => {
          setEmail("");
          report(
            sent.invite_sent
              ? `Invite sent to ${sent.email}.`
              : `${sent.email} is invited, but the email didn't send. Use Resend.`,
          );
        },
        onError: fail,
      },
    );
  }

  function confirmRemove() {
    if (!removing) return;
    const target = removing;
    remove.mutate(target.id, {
      onSuccess: () => {
        if (target.you) {
          leaveFor("/login"); // your sessions have ended
          return;
        }
        report(`${target.email} ${target.status === "invited" ? "is no longer invited" : "was removed"}.`);
      },
      onError: fail,
      onSettled: () => setRemoving(null),
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-fg">Team</h1>
        <p className="text-sm text-fg-muted">Invite people to this dashboard and manage who has access.</p>
      </div>

      <form onSubmit={handleInvite} className="flex flex-wrap items-end gap-3 rounded-lg border border-line-subtle bg-surface p-4">
        <div className="flex min-w-56 flex-1 flex-col gap-1">
          <label htmlFor="invite-email" className="text-xs text-fg-muted">Email</label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@company.com"
            className={inputCls}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="invite-role" className="text-xs text-fg-muted">Role</label>
          <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value as TeamRole)} className={inputCls}>
            {ROLES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={!email.trim() || invite.isPending}
          className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Invite
        </button>
      </form>

      {notice && (
        <div role="status" className="rounded-md border border-line-subtle bg-surface px-3 py-2 text-sm text-fg">
          {notice}
        </div>
      )}
      {error && (
        <div role="alert" className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
          {error}
        </div>
      )}

      {isLoading ? (
        <LoadingSkeleton rows={3} />
      ) : isError || !data ? (
        <ErrorState message="Couldn't load your team." onRetry={() => refetch()} />
      ) : (
        <ul className="divide-y divide-line-subtle rounded-lg border border-line-subtle bg-surface">
          {data.members.map((member) => (
            <li key={member.id} data-testid={`member-${member.email}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="text-sm text-fg">
                  {member.email}
                  {member.you && <span className="text-fg-muted"> (you)</span>}
                </p>
                <p className="text-xs text-fg-muted">
                  {ROLE_LABELS.get(member.role)} ·{" "}
                  {member.status === "invited"
                    ? "Invited, not joined yet"
                    : member.last_login_at
                      ? `Last signed in ${member.last_login_at.slice(0, 10)}`
                      : "Joined"}
                </p>
              </div>
              <div className="flex gap-2">
                {member.status === "invited" && (
                  <button
                    type="button"
                    disabled={resend.isPending}
                    onClick={() =>
                      resend.mutate(member.id, {
                        onSuccess: () => report(`Sent a new invite to ${member.email}.`),
                        onError: fail,
                      })
                    }
                    className={buttonCls}
                  >
                    Resend
                  </button>
                )}
                <button type="button" onClick={() => setRemoving(member)} className={buttonCls}>
                  {member.you ? "Leave" : member.status === "invited" ? "Cancel invite" : "Remove"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {removing && (
        <ConfirmDialog
          title={removing.you ? "Leave this team?" : `Remove ${removing.email}?`}
          message={
            removing.you
              ? "You lose access to this dashboard now and are signed out."
              : removing.status === "invited"
                ? "Their invite link stops working."
                : "They lose access now and are signed out everywhere."
          }
          confirmLabel={removing.you ? "Leave" : "Remove"}
          isPending={remove.isPending}
          onConfirm={confirmRemove}
          onCancel={() => setRemoving(null)}
          variant="danger"
        />
      )}
    </div>
  );
}
